import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from 'src/environments/environment';
import {BehaviorSubject, Observable} from 'rxjs';
import {finalize, share} from 'rxjs/operators';

import {ActivatedRoute, Router} from "@angular/router";
import {AuthInfoDTO} from "../dto/AuthInfoDTO";
import {CookieService} from "../../app/services/CookieService";
import {AbstractService} from "../../app/services/AbstractService";
import {AuthorizationCodeDTO} from "./AuthorizationCodeDTO";

// Global erişim için window üzerinde tanımlama
declare global {
    interface Window {
        globalActiveUser: AuthInfoDTO | null;
        userDataPromise: Promise<AuthInfoDTO> | null; // Kullanıcı verilerinin yüklenmesini beklemek için Promise
    }
}

export enum EnumConditionType {OR, AND }

@Injectable({providedIn: 'root'})
export abstract class AuthorizationService extends AbstractService<AuthorizationCodeDTO> {

    // Kullanıcı verilerini tutan BehaviorSubject
    private _activeUser: BehaviorSubject<AuthInfoDTO> = new BehaviorSubject(null);
    // Diğer bileşenlerin abone olabileceği Observable
    activeUser: Observable<AuthInfoDTO> = this._activeUser.asObservable();

    // Statik global kullanıcı nesnesi
    public static globalActiveUser: AuthInfoDTO | null = null;

    // Şu anda getAccountInfo api çağrısı çalışıyor mu bilgisi
    private _accountInfoLoading: boolean = false;

    // Kullanıcı verisi Promise'i
    private static userDataPromise: Promise<AuthInfoDTO> | null = null;

    override getApiURL() {
        return "/";
    }

    private userAuthorities: AuthInfoDTO[] = [];

    protected constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        protected override http: HttpClient,
        private cookieService: CookieService) {
        super(http);

        window.userDataPromise = null;
        // Initialize window.globalActiveUser
        if (!window.globalActiveUser) {
            window.globalActiveUser = new AuthInfoDTO();
        }
    }

    /**
     * Uygulama başlatılırken kullanıcı oturumunu başlatır
     * Önce localStorage/cookie'den temel bilgileri alır
     * Sonra API'den tam kullanıcı verilerini çeker
     */
    initConfig() {
        return new Promise((resolve, reject) => {
            let activeUser = this.getActiveUser();

            if (!activeUser) {
                // Önce cookie'den temel bilgileri al
                let loginUser: AuthInfoDTO;
                let cookie = this.cookieService.getCookie(this.cookieService.cookieName);
                if (cookie && cookie != "") {
                    loginUser = this.cookieService.getUserInfo(this.cookieService.cookieName);
                    if (loginUser) {
                        this.cookieService.headerToken = cookie;
                    }
                }
                if (loginUser) {
                    activeUser = loginUser;
                }
            }

            // LocalStorage'dan kullanıcı bilgilerini al
            let activeUserStringify = localStorage.getItem('activeUser');
            if (activeUserStringify) {
                activeUser = JSON.parse(activeUserStringify);
            }

            if (activeUser) {
                // Temel kullanıcı bilgilerini ayarla
                this._activeUser.next(activeUser);
                if (this.getActiveUser()) {
                    localStorage.setItem('activeUser', JSON.stringify(this.getActiveUser()));
                }

                // API'den tam kullanıcı bilgilerini al
                this.getAccountInfo().subscribe({
                    next: () => resolve(true),
                    error: () => resolve(false)
                });
            } else {
                resolve(true);
            }
        });
    }

    getAccount() {
        return this.http.get(environment.apiUrl + "account");
    }

    // Kullanıcı verisini yükleme Promise'ini alır
    // public getUserDataPromise(): Promise<AuthInfoDTO> {
    //     if (!AuthorizationService.userDataPromise) {
    //         AuthorizationService.userDataPromise = this.createUserDataPromise();
    //         window.userDataPromise = AuthorizationService.userDataPromise;
    //     }
    //
    //     return AuthorizationService.userDataPromise;
    // }

    // private createUserDataPromise(): Promise<AuthInfoDTO> {
    //     return new Promise<AuthInfoDTO>((resolve, reject) => {
    //         this.getAccountInfo().subscribe({
    //             next: (userData) => {
    //                 resolve(userData);
    //             },
    //             error: (error) => {
    //                 reject(error);
    //             }
    //         });
    //     });
    // }

    /**
     * API'den kullanıcı bilgilerini getirir
     * Eğer çağrı zaten çalışıyorsa aynı çağrı tekrar atılmaz
     */
    getAccountInfo(): Observable<AuthInfoDTO> {
        if (this._accountInfoLoading) {
            return this.activeUser;
        }

        this._accountInfoLoading = true;
        return new Observable<AuthInfoDTO>(observer => {
            this.getAccount().subscribe({
                next: (response: any) => {
                    const activeUser = new AuthInfoDTO();
                    activeUser.bindData(response);
                    activeUser.userId = response["id"];
                    activeUser.authorizationList = response.authorizationList || [];

                    this._activeUser.next(activeUser);
                    AuthorizationService.globalActiveUser = new AuthInfoDTO();
                    AuthorizationService.globalActiveUser.bindData(activeUser);

                    if (!window.globalActiveUser) {
                        window.globalActiveUser = new AuthInfoDTO();
                    }
                    window.globalActiveUser.bindData(activeUser);

                    if (this.isImpersonateUser()) {
                        localStorage.setItem('activeUser', JSON.stringify(activeUser));
                    }
                    let userIndex = this.userAuthorities.findIndex(u => u.userId == activeUser.userId);
                    if (userIndex > -1) {
                        this.userAuthorities[userIndex] = activeUser;
                    } else {
                        this.userAuthorities.push(activeUser);
                    }
                    observer.next(activeUser);
                    observer.complete();
                },
                error: (error: any) => {
                    observer.error(error);
                },
                complete: () => {
                    this._accountInfoLoading = false;
                }
            });
        }).pipe(
            // API çağrısı tamamlandığında veya hata olduğunda _accountInfoLoading'i false yap
            finalize(() => this._accountInfoLoading = false),
            // Aynı anda birden fazla abonelik olursa, hepsi aynı çağrıyı paylaşsın
            share()
        );
    }

    getAuthritiesByUserId(userid: any) {
        return this.http.get(environment.apiUrl + "/get-user-authorities/" + userid);
    }

    getUserByEmail(email: string) {
        return this.http.get(environment.apiUrl + "/find-user-by-mail/" + email);
    }

    getUsersByUserId(userId: string) {
        return this.http.get(environment.apiUrl + "/find-user-by-userid/" + userId);
    }


    getUsersByIds(group: string, userIds: any) {
        return this.http.post(environment.apiUrl + "/find-user-by-id/" + group, userIds);
    }

    getUsersByPaperAssignable(size: any, page: any, sortField: any, sortType: string, data: {}) {
        return this.http.get(`${environment.apiUrl}/find-user-by-paper-assignable?size=${size}&page=${page}&sortField=${sortField}&sortType=${sortType}&searchKey=${data}`);
    }

    /**
     * Kullanıcı bilgilerini ayarlar (sadece temel bilgilerle veya tam verilerle)
     * Her durumda BehaviorSubject ve global değişkenleri günceller
     */
    setActiveUser(activeUser: AuthInfoDTO) {
        let user = this.userAuthorities.find(u => u.userId == activeUser.userId);
        if (user) {
            // Kullanıcı zaten authorities listesinde var
            this._activeUser.next(user);

            // Global değişkenleri güncelle
            AuthorizationService.globalActiveUser = new AuthInfoDTO();
            AuthorizationService.globalActiveUser.bindData(user);

            if (!window.globalActiveUser) {
                window.globalActiveUser = new AuthInfoDTO();
            }
            window.globalActiveUser.bindData(user);


            // Kullanıcı bilgilerini localStorage'a kaydet
            if (this.getActiveUser()) {
                localStorage.setItem('activeUser', JSON.stringify(this.getActiveUser()));
            } else {
                localStorage.removeItem('activeUser');
            }
        } else {
            // Yeni kullanıcı veya henüz authorities listesinde olmayan kullanıcı
            this._activeUser.next(activeUser);

            // Global değişkenleri güncelle
            AuthorizationService.globalActiveUser = new AuthInfoDTO();
            AuthorizationService.globalActiveUser.bindData(activeUser);

            if (!window.globalActiveUser) {
                window.globalActiveUser = new AuthInfoDTO();
            }
            window.globalActiveUser.bindData(activeUser);

            // Kullanıcı bilgilerini localStorage'a kaydet
            if (this.getActiveUser()) {
                localStorage.setItem('activeUser', JSON.stringify(this.getActiveUser()));
            } else {
                localStorage.removeItem('activeUser');
            }

            // API'den güncel bilgilerle kullanıcıyı yeniden al
            // Bu işlem için initConfig veya getAccountInfo zaten çağrılmış olabilir
            // Bu yüzden bu çağrıyı burada yapmaya gerek yok
        }
    }

    getActiveUser() {
        return this._activeUser.getValue();
    }

    // Global kullanıcı bilgisine statik erişim sağlayan yardımcı metot
    public static getGlobalActiveUser(): AuthInfoDTO | null {
        return AuthorizationService.globalActiveUser;
    }


    getActiveUserBS() {
        return this._activeUser;
    }


    resetActiveUser() {
        localStorage.removeItem('activeUser');
        this._activeUser.next(null);

        // Global değişkenleri sıfırla
        AuthorizationService.globalActiveUser = null;
        window.globalActiveUser = null;

        this.userAuthorities = [];
    }

    hasPermission(permissons?: string[]): boolean {
        let hasPerm: boolean = false;

        if (permissons) {
            permissons = typeof permissons === 'string' ? [permissons] : permissons;
        }

        if (permissons && this.getActiveUser() && this.getActiveUser().authorizationList) {
            permissons.forEach(perm => {
                if (this.getActiveUser().authorizationList?.includes(perm)) {
                    hasPerm = true;
                }
            });
        }

        return hasPerm;
    }

    hasPermissionAll(permissons?: string[]): boolean {
        let hasPerm: boolean = false;

        if (permissons) {
            permissons = typeof permissons === 'string' ? [permissons] : permissons;
        }

        if (permissons && this.getActiveUser() && this.getActiveUser().authorizationList) {
            hasPerm = permissons.every(e => this.getActiveUser().authorizationList?.indexOf(e) !== -1);
        }

        return hasPerm;
    }

    isImpersonateUser() {
        let isImpersonateUser = false;
        let loginUser: AuthInfoDTO;
        let cookie = this.cookieService.getCookie(this.cookieService.cookieName);
        loginUser = this.cookieService.getUserInfo(this.cookieService.cookieName);
        if (loginUser) {
            let activeUser = this.getActiveUser();
            if (activeUser && activeUser.userId != loginUser.userId) {
                isImpersonateUser = true;
            }
        }

        return isImpersonateUser;
    }
}
